import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";

interface TcStatRingProps {
  passed: number;
  failed?: number;
  total: number;
}

/**
 * Compact, crisp pass-rate ring for the TC STAT column. Uses a plain SVG arc
 * (not a tiny PieChart) so the centered label is always perfectly aligned.
 */
const TcStatRing: React.FC<TcStatRingProps> = ({ passed, total }) => {
  if (!total) {
    return (
      <Tooltip arrow title="No cases yet">
        <Typography
          sx={{ color: "text.disabled", fontWeight: 700, fontSize: "0.9rem" }}
        >
          —
        </Typography>
      </Tooltip>
    );
  }

  const pct = Math.round((passed / total) * 100);
  const color =
    pct === 100 ? "#16a34a" : passed === 0 ? "#dc2626" : "#d97706";

  const size = 46;
  const stroke = 5;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (pct / 100) * c;

  return (
    <Tooltip arrow title={`${passed} of ${total} passed · ${pct}% pass rate`}>
      <Box
        sx={{ position: "relative", width: size, height: size, display: "inline-flex" }}
      >
        <svg width={size} height={size}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke="#E4E7EC"
            strokeWidth={stroke}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${dash} ${c - dash}`}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={{ transition: "stroke-dasharray 300ms ease" }}
          />
        </svg>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 800, color }}>
            {pct}%
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
};

export default TcStatRing;
